import { captureSafeException, type SafeFields } from "@/lib/sentry-instrument";
import { ApiError } from "./client";

const WORKSPACE_LIST_ROUTE = "/dashboard/workspaces";
const WORKSPACE_LIST_EVENT = "workspace.list_query_failed";
const WORKSPACE_LIST_OPERATION = "workspace_list_query";

let capturedWorkspaceListErrors = new WeakSet<object>();
const capturedPrimitiveWorkspaceListErrors = new Set<string>();

export interface WorkspaceListQueryErrorMeta {
  retryCount?: number;
}

function runtimeFields(): SafeFields {
  const fields: SafeFields = {
    operation: WORKSPACE_LIST_OPERATION,
    route: WORKSPACE_LIST_ROUTE,
    transaction: WORKSPACE_LIST_ROUTE,
    service: "frontend",
  };

  const environment =
    import.meta.env.VITE_ENVIRONMENT_NAME ?? import.meta.env.MODE;
  if (typeof environment === "string" && environment.length > 0) {
    fields.environment = environment;
  }

  const release = import.meta.env.VITE_SENTRY_RELEASE;
  if (typeof release === "string" && release.length > 0) {
    fields.release = release;
  }

  return fields;
}

function statusFromError(error: unknown): number | undefined {
  if (error instanceof ApiError && typeof error.status === "number") {
    return error.status;
  }
  if (typeof error !== "object" || error === null) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

function reasonFromError(error: unknown): string {
  if (error instanceof ApiError) return "api_error";
  if (error instanceof TypeError) return "network_error";
  return "query_error";
}

function sanitizedWorkspaceListError(): Error {
  const err = new Error("Workspace list query failed");
  err.name = "WorkspaceListQueryError";
  return err;
}

function wasAlreadyCaptured(error: unknown): boolean {
  if (
    (typeof error === "object" && error !== null) ||
    typeof error === "function"
  ) {
    const key = error as object;
    if (capturedWorkspaceListErrors.has(key)) return true;
    capturedWorkspaceListErrors.add(key);
    return false;
  }

  const primitiveKey = typeof error;
  if (capturedPrimitiveWorkspaceListErrors.has(primitiveKey)) return true;
  capturedPrimitiveWorkspaceListErrors.add(primitiveKey);
  return false;
}

export function captureWorkspaceListQueryError(
  error: unknown,
  meta: WorkspaceListQueryErrorMeta = {},
): void {
  if (wasAlreadyCaptured(error)) return;

  const fields = runtimeFields();
  fields.reason = reasonFromError(error);

  const status = statusFromError(error);
  if (status !== undefined) {
    fields.status = status;
    fields.status_code = status;
  }

  if (typeof meta.retryCount === "number") {
    fields.retry_count = meta.retryCount;
  }

  captureSafeException(
    sanitizedWorkspaceListError(),
    WORKSPACE_LIST_EVENT,
    fields,
  );
}

export function __resetWorkspaceListQueryCaptureForTests(): void {
  capturedWorkspaceListErrors = new WeakSet<object>();
  capturedPrimitiveWorkspaceListErrors.clear();
}
